import { ProcessedWaterfallData } from './WaterfallChart.types';

/**
 * Rendered bar width in px. Fixed by design and never scaled to the category count, matching the
 * vertical bar chart standard. Applied through the G2 interval's `minWidth`/`maxWidth` style, which
 * clamps the band-derived width while keeping the bar centered on its slot.
 */
export const WATERFALL_BAR_WIDTH_PX = 16;

/**
 * Minimum width in px of one bar's slot — the 16px bar plus its share of the surrounding gap. At
 * this floor the visible gap between two adjacent bars is 56px. When the categories can no longer
 * each be given a slot this wide the chart scrolls instead of squeezing the bars.
 */
export const WATERFALL_MIN_SLOT_WIDTH_PX = 72;

/** Horizontal space in px reserved inside the canvas for the y-axis title, ticks and labels. */
export const WATERFALL_Y_AXIS_RESERVE_PX = 72;

/** Combined left + right padding in px of the chart card around the canvas. */
export const WATERFALL_CHART_HORIZONTAL_PADDING_PX = 32;

/**
 * Vertical space in px reserved below the x-axis labels for the horizontal scrollbar track, added
 * to the canvas `paddingBottom` only while scrolling.
 */
export const WATERFALL_SCROLLBAR_RESERVE_PX = 24;

/**
 * Floor for the computed plot width so a container that has not been measured yet (width 0 on the
 * first paint, before the ResizeObserver fires) still yields a usable, non-negative layout.
 */
const MIN_PLOT_WIDTH_PX = 80;

export interface WaterfallSlotLayout {
    /** Width in px available to the bars, after the y-axis and card-padding reserves. */
    plotWidthPx: number;
    /** Number of slots the plot area can show at or above the minimum slot width. */
    visibleSlotCount: number;
    /** Rendered slot width in px: `max(WATERFALL_MIN_SLOT_WIDTH_PX, plotWidthPx / barCount)`. */
    slotWidthPx: number;
    /** Visible gap in px between two adjacent bars at the rendered slot width. */
    barGapPx: number;
    /** True when the bars cannot all be shown without dropping below the minimum slot width. */
    needsScroll: boolean;
    /** Fraction of the category domain the scrollbar window exposes, for G2's `scrollbar.x.ratio`. */
    scrollbarRatio: number;
}

/**
 * Resolve the waterfall's horizontal geometry from the measured container width.
 *
 * The bar width is a constant, so the category count changes only the gaps: with few bars the slot
 * expands and the bars spread out; past the point where every bar can hold a
 * `WATERFALL_MIN_SLOT_WIDTH_PX` slot the layout stops compressing and reports `needsScroll`, and
 * the slot width settles at whatever the widest whole number of visible slots yields.
 *
 * @param barCount Number of bars (categories) in the processed dataset.
 * @param containerWidthPx Measured width of the chart wrapper element.
 */
export function computeWaterfallSlotLayout(barCount: number, containerWidthPx: number): WaterfallSlotLayout {
    const count = Math.max(1, Math.floor(barCount) || 1);
    const plotWidthPx = Math.max(
        MIN_PLOT_WIDTH_PX,
        (Number.isFinite(containerWidthPx) ? containerWidthPx : 0) -
            WATERFALL_Y_AXIS_RESERVE_PX -
            WATERFALL_CHART_HORIZONTAL_PADDING_PX,
    );

    const slotsThatFit = Math.max(1, Math.floor(plotWidthPx / WATERFALL_MIN_SLOT_WIDTH_PX));
    const visibleSlotCount = Math.min(count, slotsThatFit);
    const slotWidthPx = plotWidthPx / visibleSlotCount;

    return {
        plotWidthPx,
        visibleSlotCount,
        slotWidthPx,
        barGapPx: Math.max(0, slotWidthPx - WATERFALL_BAR_WIDTH_PX),
        needsScroll: count > visibleSlotCount,
        scrollbarRatio: Math.min(1, visibleSlotCount / count),
    };
}

/**
 * Vertical space in px a value label needs above its bar: the label's own 12px height plus the 5px
 * `dy` offset, rounded up for the descender-free cap height G2 measures against.
 */
export const WATERFALL_VALUE_LABEL_HEADROOM_PX = 20;

/**
 * Vertical space in px needed above a bar when the percentage label sits above the value label: the
 * percentage row's 20px `dy` offset plus its 11px height, rounded up.
 */
export const WATERFALL_STACKED_LABEL_HEADROOM_PX = 36;

/**
 * Extend a value range so a fixed pixel band stays free above the tallest bar.
 *
 * Labels above the bars are drawn in the plot area, and once the scrollbar is engaged G2 clips the
 * plot area (its slider filter sets `clip: true` on the view), which trims any label that overflows
 * the top. Rather than relying on canvas padding — which the clip ignores — the value scale is given
 * enough headroom in *data* units that the label band always falls inside the plot.
 *
 * @param processedData Processed waterfall rows; the domain spans their starts and ends plus zero.
 * @param plotHeightPx Height in px of the plot area (canvas height minus top and bottom padding).
 * @param headroomPx Pixels to keep free above the tallest bar for its label stack.
 * @returns The padded `[min, max]` domain, or `undefined` when the geometry cannot support headroom
 *          (no data, a flat series, or a plot shorter than the label band) and G2 should infer it.
 */
export function computeWaterfallValueDomain(
    processedData: ProcessedWaterfallData[],
    plotHeightPx: number,
    headroomPx: number,
): [number, number] | undefined {
    if (processedData.length === 0) return undefined;
    if (!Number.isFinite(plotHeightPx) || !Number.isFinite(headroomPx) || headroomPx < 0) return undefined;

    const usableHeightPx = plotHeightPx - headroomPx;
    if (usableHeightPx <= 0) return undefined;

    const values = processedData.flatMap((d) => [d.start, d.end]).filter((value) => Number.isFinite(value));
    if (values.length === 0) return undefined;

    const min = Math.min(0, ...values);
    const max = Math.max(0, ...values);
    const range = max - min;
    if (range <= 0) return undefined;

    // The padded range maps onto the full plot height, so `headroomPx` of it must be the extension:
    // headroomPx / plotHeightPx = extra / (range + extra).
    return [min, max + (range * headroomPx) / usableHeightPx];
}

/** One dashed connector spanning the gap between bar `index` and bar `nextIndex`. */
export interface WaterfallConnectorDatum {
    /** Band value of the bar the connector leaves — the same `index` field the bars encode on. */
    index: number;
    /** Band value of the bar the connector reaches. */
    nextIndex: number;
    /** Y value at the departing bar: the running total after it. */
    connectorStart: number;
    /** Y value at the arriving bar: the running total it builds from. */
    connectorEnd: number;
}

/**
 * Build the connector rows for a processed waterfall series.
 *
 * Every row is keyed by the same `index` band values the bars use, so a single G2 `link` mark drawn
 * from this data shares the bars' x scale. That is what keeps connectors in step with the scrollbar:
 * G2's scrollbar filter narrows the view's x domain across *every* mark, so windowing the bars
 * windows the connectors with them.
 */
export function buildWaterfallConnectorData(processedData: ProcessedWaterfallData[]): WaterfallConnectorDatum[] {
    const connectors: WaterfallConnectorDatum[] = [];

    for (let i = 0; i < processedData.length - 1; i++) {
        connectors.push({
            index: i,
            nextIndex: i + 1,
            connectorStart: processedData[i].end,
            connectorEnd: processedData[i + 1].start,
        });
    }

    return connectors;
}
